import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { ProductMultiplierTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { ProductMultiplierTypeFilters } from '@/types'
import { Button } from '../base/button'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { DeleteDialog } from '../delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'

interface Props extends TableProps {
  filters?: ProductMultiplierTypeFilters
  createable?: boolean
  deleteable?: boolean
}

export const Table = ({ pageSize = 10, filters = {}, createable = false, deleteable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<ProductMultiplierTypesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openDelete,
    setOpenDelete
  } = useTable({ pageInfo: data?.productMultiplierTypes.pageInfo })
  const [productMultiplierTypeId, setProductMultiplierTypeId] = useState<string>()

  const deleteProductMultiplierType = (id: string) => {
    if (!deleteable) return

    setProductMultiplierTypeId(id)
    setOpenDelete(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<ProductMultiplierTypeFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Summary' }, { name: '' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.productMultiplierTypes.nodes = dedupe([
              ...previousData.productMultiplierTypes.nodes,
              ...fetchMoreResult.productMultiplierTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.productMultiplierTypes.nodes.map(pm => (
          {
            id: pm.id,
            columns: [
              pm.multiplierType.summary,
              deleteable
                ? <div className="text-right -mx-1">
                  <Button color="red" onClick={() => deleteProductMultiplierType(pm.id) }>Delete</Button>
                </div>
                : <></>
            ]
          }
        ))}
      />
      { productMultiplierTypeId && (<DeleteDialog
        id={productMultiplierTypeId}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[Query]}
        setOpen={setOpenDelete}
        onSuccess={
          () => {
            setOpenDelete(false)
            setProductMultiplierTypeId(undefined)
          }
        }
      />) }
      { filters.productIdEq &&
        filters.productTypeEq &&
        <NewDialog
          defaultValues={{ productId: filters.productIdEq, productType: filters.productTypeEq }}
          open={openNew}
          setOpen={setOpenNew}
        /> }
    </>
  )
}
